import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
  useCallback,
} from 'react';

import { Row, Col, Input, Space, Alert, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Form, StyledModal } from 'common/components';
import {
  SearchGrid,
  SearchGridItemButton,
  SectionContainer,
} from '../components';
import OverlayOptions, { OVERLAY_OPTIONS } from './OverlayOptions';
import MemberMapping from './MemberMapping';

import { useQueryMembersForCreateOrRetargetMember } from './queries';
import { useCheckPermissions, permissions } from 'hooks/useCheckPermissions';
import { useUserInfo } from 'hooks';

import reactCSS from 'reactcss';
import classnames from 'classnames';

import './styles.less';

const DEFAULT_OVERLAY_OPTION = OVERLAY_OPTIONS[0];

const slideInRight = classnames(
  'ani--zoomIn',
  'ani--fill-forward',
  'ani--iter-1',
  'ani--easy',
  'ani--time-70'
);

const CreateOrRetargetProductOverlayModal = (props, ref) => {
  const {
    loading,
    title,
    savedItem,
    service,
    onSubmit,
    isMultiple = true,
    onlyFetchWithSearchText = true,
    target = DEFAULT_OVERLAY_OPTION?.value,
    ...rest
  } = props;
  const isAllowToViewOverlayProvideData = useCheckPermissions(
    permissions.PERMISSION_VIEW_OVERLAY_PROVIDE_DATA
  );

  const getInitOverlayOption = () => {
    if (!isAllowToViewOverlayProvideData) {
      return DEFAULT_OVERLAY_OPTION.value;
    }
    return target;
  };

  const refSearchText = useRef();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [overlayOption, setOverlayOption] = useState(getInitOverlayOption);

  const [searchText, setSearchText] = useState('');

  const {
    userInfo: { member: originMember },
  } = useUserInfo();

  const isNonTargetMemberMode = overlayOption === DEFAULT_OVERLAY_OPTION.value;

  const [formInstance] = Form.useForm();

  const {
    data: searchMemberResonse,
    isInitialLoading: searchMemberInitLoading,
    isLoading: searchMemberLoading,
  } = useQueryMembersForCreateOrRetargetMember(searchText, {
    enabled: !!searchText,
  });

  const searchedMembers = useMemo(() => {
    return (
      searchMemberResonse?.allMembers.map((memberItem) => {
        const { status, ...newMemberInfo } = memberItem || {};
        return newMemberInfo;
      }) || []
    );
  }, [searchMemberResonse]);

  const handleSearchItem = () => {
    const searchTextValue = refSearchText.current;
    setSearchText(searchTextValue);
  };

  const handleClearSearch = () => {
    formInstance.resetFields();
  };

  const handleSelectItem = (itemData) => {
    setSelectedItems((prev) => {
      let nextValue = [...prev];

      const foundItemIdx = nextValue.findIndex(
        (item) =>
          (item?.companyId || item?.id) ===
          (itemData?.companyId || itemData?.id)
      );

      if (foundItemIdx !== -1) {
        nextValue.splice(foundItemIdx, 1);
      } else {
        if (!isMultiple) {
          nextValue = [itemData];
        } else {
          nextValue.push(itemData);
        }
      }

      return nextValue;
    });
  };

  const handleClickOk = () => {
    onSubmit &&
      onSubmit({
        payload: selectedItems,
      });
  };

  const resetAll = useCallback(() => {
    formInstance.resetFields();
    setOverlayOption(getInitOverlayOption());
    setSearchText('');
    setSelectedItems([]);
  }, []);

  const onChangeSearchText = (e) => {
    refSearchText.current = e.target.value;
  };

  const onRemoveTargetMember = () => {
    setSelectedItems([]);
  };

  const onChangeOverlayOption = (e) => {
    const value = e.target.value;
    if (value === DEFAULT_OVERLAY_OPTION.value) {
      setSelectedItems([originMember]);
      setSearchText('');
    } else {
      setSelectedItems([]);
    }
    setOverlayOption(value);
  };

  const makeGridItemConfig = (item) => {
    if (originMember?.id === item?.companyId) {
      return {
        style: {
          opacity: '0.5',
          pointerEvent: 'none',
        },
        disableClick: true,
      };
    } else return {};
  };

  useEffect(() => {
    if (!isModalOpen) {
      resetAll();
    }
  }, [isModalOpen, resetAll]);

  useEffect(() => {
    if (overlayOption === DEFAULT_OVERLAY_OPTION.value) {
      setSelectedItems([originMember]);
    } else {
      setSelectedItems([]);
    }
  }, [target, originMember, overlayOption]);

  useImperativeHandle(ref, () => ({
    closeModal: () => {
      setIsModalOpen(false);
    },
    openModal: () => {
      setIsModalOpen(true);
    },
  }));

  const showSearchMembers = overlayOption !== DEFAULT_OVERLAY_OPTION?.value;

  const modalStyle = reactCSS({
    default: {
      bodyStyle: {
        height: 460,
      },
      width: showSearchMembers ? 1150 : 468,
      style: {
        transition: 'width 0.3s ease-out 0s',
      },
    },
  });

  return (
    <>
      <StyledModal
        visible={isModalOpen}
        className='search-grid-item-create-product-overlay__modal'
        title={title}
        onCancel={() => {
          setIsModalOpen(false);
        }}
        width={modalStyle.width}
        onOk={handleClickOk}
        centered
        destroyOnClose
        maskClosable={false}
        okButtonProps={{
          disabled: isNonTargetMemberMode ? false : !selectedItems?.length,
        }}
        confirmLoading={loading}
        style={modalStyle.style}
        bodyStyle={modalStyle.bodyStyle}
        {...rest}
      >
        {showSearchMembers && (
          <Row className={{ [slideInRight]: showSearchMembers }}>
            <Col>
              <Input
                style={{ width: 270 }}
                onPressEnter={handleSearchItem}
                allowClear
                onChange={onChangeSearchText}
              />
            </Col>
            <Col>
              <Space>
                <SearchGridItemButton type='primary' onClick={handleSearchItem}>
                  <SearchOutlined />
                </SearchGridItemButton>

                {!onlyFetchWithSearchText && (
                  <SearchGridItemButton
                    type='primary'
                    onClick={handleClearSearch}
                  >
                    Reset
                  </SearchGridItemButton>
                )}
              </Space>
            </Col>
          </Row>
        )}

        <Row
          style={{
            flex: 1,
            marginLeft: -5,
            marginRight: -5,
            overflow: 'hidden',
          }}
        >
          {showSearchMembers && (
            <Col
              className={{ [slideInRight]: showSearchMembers }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <SectionContainer>
                <SearchGrid
                  items={searchedMembers}
                  handleSelectItem={handleSelectItem}
                  selectedItems={selectedItems}
                  isLoading={searchMemberInitLoading && searchMemberLoading}
                  searchType='member'
                  itemIdName='companyId'
                  showSearchHint={
                    searchedMembers?.length === 0 && searchText === ''
                  }
                  makeGridItemConfig={makeGridItemConfig}
                />
              </SectionContainer>
            </Col>
          )}
          <Col
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: 445,
            }}
          >
            <Row>
              <Col xs={24}>
                <Col>
                  <SectionContainer>
                    <OverlayOptions
                      onChange={onChangeOverlayOption}
                      value={overlayOption}
                      isAllowToViewOverlayProvideData={
                        isAllowToViewOverlayProvideData
                      }
                    />
                  </SectionContainer>
                </Col>
              </Col>
            </Row>
            <Row style={{ flex: 1 }}>
              <Col xs={24}>
                <SectionContainer>
                  <Alert
                    message='Informational Note'
                    description={
                      <>
                        <Typography.Paragraph>
                          {overlayOption === OVERLAY_OPTIONS[0]?.value
                            ? `Overlay data will be applied for the current user's member.`
                            : `Overlay data will be applied for the selected target member.`}
                        </Typography.Paragraph>
                      </>
                    }
                    type='info'
                    showIcon
                    style={{ height: '100%', paddingTop: 8 }}
                  />
                </SectionContainer>
              </Col>
            </Row>
            <Row>
              <Col xs={24}>
                <SectionContainer>
                  <MemberMapping
                    showSearchMembers={showSearchMembers}
                    originMember={originMember}
                    targetMember={selectedItems?.[0]}
                    onRemoveTargetMember={onRemoveTargetMember}
                    hideDeleteTargetMemberBtn={
                      isNonTargetMemberMode ? true : false
                    }
                  />
                </SectionContainer>
              </Col>
            </Row>
          </Col>
        </Row>
      </StyledModal>
    </>
  );
};

export default forwardRef(CreateOrRetargetProductOverlayModal);
